/**
 * Wave-Viz 4：财务/管理视角「本月回款 vs 目标」进度卡（V-FE-dashboard）
 *
 * 调 GET /dashboard/receivable-target，端点契约：{ target, received, receivable, byTeam? }
 *   - target：月目标 = teams.monthly_target 按 data_scope 汇总（manager 限本团队、admin/director/finance 全公司）。
 *     缺失/为 0 时前端显「未设置」、不渲染百分比（不硬编 20万/80万）。
 *   - received：本月已收（payment_records status='approved' + paid_at∈月，退款冲减）。
 *   - receivable：应收对照（口径由后端定，前端只展示）。
 *   - byTeam?：可选分团队明细。
 * 后端 @RequirePermissions('dashboard:finance_workbench' 等可见点) + scope，sales 不命中。
 *
 * 挂 admin/director/manager 可见的工作台位（财务视图 DirectorFinanceDashboardView 或通用管理视图）。
 * 金额展示用 formatMoneyShort（万口径，与看板一致）；进度条复用 InlineProgress 同款配色逻辑。
 */
import { useQuery } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { HomeCard } from '../ui';
import { formatMoneyShort } from '../../lib/format';

type ReceivableTarget = {
  target: number;
  received: number;
  receivable: number;
  byTeam?: Array<{ teamId: number; teamName: string; target: number; received: number }>;
};

export function ReceivableTargetCard() {
  const { data } = useQuery<ReceivableTarget>({
    queryKey: ['dashboard/receivable-target'],
    queryFn: () => $.get('/dashboard/receivable-target'),
    refetchInterval: 120_000,
  });

  if (!data) return null;
  const target = Number(data.target ?? 0);
  const received = Number(data.received ?? 0);
  const receivable = Number(data.receivable ?? 0);
  const targetSet = target > 0;
  const rate = targetSet ? received / target : 0;
  const ratePct = (rate * 100).toFixed(1);
  // 达标配色：>=100% 绿 / >=80% 蓝 / >=50% 橙 / 其余红
  const rateColor =
    rate >= 1 ? 'var(--success)' :
    rate >= 0.8 ? '#1677ff' :
    rate >= 0.5 ? 'var(--warning-text)' : 'var(--danger)';

  return (
    <HomeCard
      title={<>本月回款 vs 目标 <span style={{ fontSize: 12, color: 'var(--text-3)', fontWeight: 400, marginLeft: 6 }}>已收 / 月目标</span></>}
    >
      <div style={{ padding: '12px 16px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <div>
            <span style={{ fontSize: 24, fontWeight: 600, color: targetSet ? rateColor : 'var(--text-1)' }}>
              {formatMoneyShort(received)}
            </span>
            <span style={{ marginLeft: 8, fontSize: 13, color: 'var(--text-3)' }}>
              / {targetSet ? formatMoneyShort(target) : '未设置'}
            </span>
          </div>
          {targetSet && (
            <div style={{ fontSize: 14, fontWeight: 500, color: rateColor }}>{ratePct}%</div>
          )}
        </div>
        {targetSet ? (
          <div style={{ background: 'var(--bg-3)', height: 10, borderRadius: 5, overflow: 'hidden', marginBottom: 12 }}>
            <div style={{
              width: `${Math.min(100, rate * 100)}%`, height: '100%',
              background: rateColor, transition: 'width 0.4s',
            }} />
          </div>
        ) : (
          <div style={{
            background: 'rgba(0,0,0,0.03)', color: 'var(--text-3)',
            padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13,
          }}>
            月目标未设置 — 请在「团队管理」配置 monthly_target。
          </div>
        )}
        <div style={{ display: 'flex', gap: 24, fontSize: 13, color: 'var(--text-2)' }}>
          <div>
            <div style={{ color: 'var(--text-3)', fontSize: 12 }}>本月已收</div>
            <div style={{ fontWeight: 500 }}>{formatMoneyShort(received)}</div>
          </div>
          <div>
            <div style={{ color: 'var(--text-3)', fontSize: 12 }}>应收对照</div>
            <div style={{ fontWeight: 500 }}>{formatMoneyShort(receivable)}</div>
          </div>
          {targetSet && (
            <div>
              <div style={{ color: 'var(--text-3)', fontSize: 12 }}>距目标</div>
              <div style={{ fontWeight: 500 }}>
                {received >= target ? '✓ 已达成' : formatMoneyShort(target - received)}
              </div>
            </div>
          )}
        </div>
        {/* 可选分团队明细 */}
        {Array.isArray(data.byTeam) && data.byTeam.length > 0 && (
          <div style={{ marginTop: 12, borderTop: '1px dashed var(--border)', paddingTop: 8 }}>
            {data.byTeam.map((t) => {
              const tSet = Number(t.target) > 0;
              const tRate = tSet ? Number(t.received) / Number(t.target) : 0;
              return (
                <div key={t.teamId} style={{ padding: '6px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-3)', marginBottom: 3 }}>
                    <span style={{ color: 'var(--text-2)' }}>{t.teamName}</span>
                    <span>{formatMoneyShort(t.received)} / {tSet ? formatMoneyShort(t.target) : '未设置'}</span>
                  </div>
                  {tSet && (
                    <div style={{ background: 'var(--bg-3)', height: 5, borderRadius: 3 }}>
                      <div style={{
                        width: `${Math.min(100, tRate * 100)}%`, height: '100%',
                        background: tRate >= 1 ? 'var(--success)' : tRate >= 0.5 ? '#1677ff' : 'var(--warning-text)',
                        borderRadius: 3,
                      }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </HomeCard>
  );
}
